import type { WidgetMore, WidgetSearchQuery, WidgetSectionItem, WidgetTarget } from "@gerama/types";

/** Site/admin URL of a search with these filters — the search page reads exactly these params. */
export function searchHref(search: WidgetSearchQuery): string {
  const params = new URLSearchParams();
  if (search.q?.trim()) params.set("q", search.q.trim());
  if (search.type) params.set("type", search.type);
  if (search.categoryIds?.length) params.set("categoryIds", search.categoryIds.join(","));
  if (search.artistIds?.length) params.set("artistIds", search.artistIds.join(","));
  if (search.yearFrom) params.set("yearFrom", String(search.yearFrom));
  if (search.yearTo) params.set("yearTo", String(search.yearTo));
  if (search.sort && search.sort !== "relevance") params.set("sort", search.sort);
  const qs = params.toString();
  return qs ? `/search?${qs}` : "/search";
}

const CONTENT_PATHS: Record<string, string> = {
  song: "/songs",
  video: "/videos",
  album: "/albums",
  playlist: "/playlists",
  artist: "/artists",
  category: "/categories",
  podcast: "/podcasts",
};

export function targetHref(target: WidgetTarget | undefined): string | null {
  if (!target) return null;
  if (target.type === "url") return target.url || null;
  if (target.type === "search") return searchHref(target.search ?? {});
  const base = CONTENT_PATHS[target.type];
  if (
    (target.type === "artist" || target.type === "song" || target.type === "album" || target.type === "video") &&
    target.code &&
    base
  ) {
    return `${base}/${target.code}`;
  }
  return base && target.id != null ? `${base}/${target.id}` : null;
}

/** Where tapping an item goes — its `target`, or (older payloads) its type/id/targetUrl. */
export function itemHref(item: WidgetSectionItem): string | null {
  const fromTarget = targetHref(item.target);
  if (fromTarget) return fromTarget;
  if (item.type === "slide" || item.type === "banner") return item.targetUrl ?? null;
  const base = CONTENT_PATHS[item.type];
  return base && typeof item.id === "number" ? `${base}/${item.id}` : null;
}

/** Song/video items (and slides/banners pointing at one) start playback instead of just opening. */
export function isPlayable(item: WidgetSectionItem): boolean {
  const type = item.target?.type ?? item.type;
  return type === "song" || type === "video";
}

export function moreHref(more: WidgetMore | null | undefined): string | null {
  if (!more) return null;
  if (more.type === "category" && more.categoryId) return `/categories/${more.categoryId}`;
  if (more.type === "url") return more.url || null;
  if (more.type === "search")
    return searchHref(more.search ?? (more.query ? { q: more.query } : {}));
  return null;
}
